
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Search, AlertTriangle, Loader2 } from "lucide-react";
import { getActiveWidgetsForDashboard } from "@/services/widgetManager";
import { WidgetDefinition } from "@/components/admin/dashboard/widget-types";
import { toast } from "@/hooks/use-toast";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

interface WidgetsModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeWidgetIds: string[];
  onAddWidget: (widgetId: string, size: 'small' | 'medium' | 'large' | 'xlarge') => void;
  dashboardType: 'admin' | 'user';
}

export function WidgetsModal({
  isOpen,
  onClose,
  activeWidgetIds,
  onAddWidget,
  dashboardType,
}: WidgetsModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [availableWidgets, setAvailableWidgets] = useState<WidgetDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Fetch active widget definitions when the modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchWidgets = async () => {
        setIsLoading(true);
        try {
          console.log(`Fetching widgets for dashboard type: ${dashboardType}`);
          const widgets = await getActiveWidgetsForDashboard(dashboardType);
          
          // Verify if the financial-result widget is present
          if (dashboardType === 'user') {
            const hasFinancialResult = widgets.some(widget => widget.id === 'financial-result');
            if (!hasFinancialResult) {
              console.warn("Financial Result widget is missing from available widgets!");
            } else {
              console.log("Financial Result widget is available in the modal");
            }
          }
          
          console.log(`Fetched ${widgets.length} widgets for ${dashboardType} dashboard:`, 
            widgets.map(w => ({ id: w.id, title: w.title })));
          setAvailableWidgets(widgets);
        } catch (error) {
          console.error('Error fetching widget definitions:', error);
          setAvailableWidgets([]);
          toast({
            title: "Error",
            description: "Failed to load available widgets",
            variant: "destructive",
          });
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchWidgets();
    }
  }, [isOpen, dashboardType]);
  
  const filteredWidgets = availableWidgets.filter(widget => 
    widget.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    widget.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    widget.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] h-[60vh] flex flex-col">
        <DialogHeader className="pb-2">
          <DialogTitle>Add Widgets</DialogTitle>
          <DialogDescription>
            Select widgets to add to your dashboard.
          </DialogDescription>
        </DialogHeader>
        
        <div className="relative mb-4">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search widgets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
        
        <ScrollArea className="flex-1 overflow-y-auto pr-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Loading available widgets...</span>
            </div>
          ) : availableWidgets.length === 0 ? (
            <Alert variant="warning" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>No Widgets Available</AlertTitle>
              <AlertDescription>
                No widget definitions were found. Please contact your administrator to initialize the default widgets in the Widget Designer.
              </AlertDescription>
            </Alert>
          ) : filteredWidgets.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No widgets found matching "{searchQuery}"
            </div>
          ) : (
            <div className="space-y-4 pb-2">
              {filteredWidgets.map((widget) => {
                const isAlreadyAdded = activeWidgetIds.includes(widget.id);
                
                return (
                  <div
                    key={widget.id}
                    className="border rounded-lg p-4 space-y-2 hover:border-primary transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium">{widget.title}</h3>
                      <div className="text-xs bg-secondary text-secondary-foreground rounded px-2 py-1">
                        {widget.defaultSize}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {widget.description}
                    </p>
                    <div className="pt-2">
                      <Button
                        size="sm"
                        variant={isAlreadyAdded ? "secondary" : "default"}
                        onClick={() => onAddWidget(widget.id, widget.defaultSize)}
                        disabled={isAlreadyAdded}
                      >
                        {isAlreadyAdded ? "Already Added" : "Add to Dashboard"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
